import React, { useEffect, useState } from "react";
import { Phone, MapPin, Clock, X } from "lucide-react";
import { api, formatPrice } from "../../lib/api";

const STATUSES = [
  { key: "pending_call", label: "À rappeler" },
  { key: "confirmed", label: "Confirmée" },
  { key: "preparing", label: "Préparation" },
  { key: "en_route", label: "En livraison" },
  { key: "delivered", label: "Livrée" },
  { key: "cancelled", label: "Annulée" },
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    const url = filter === "all" ? "/admin/orders" : `/admin/orders?status=${filter}`;
    api.get(url).then((r) => {
      setOrders(r.data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const handleStatusChange = async (orderId, status) => {
    await api.put(`/admin/orders/${orderId}`, { status });
    if (selected?.id === orderId) {
      setSelected({ ...selected, status });
    }
    load();
  };

  return (
    <div data-testid="admin-orders-page">
      <p className="eyebrow mb-2">Service</p>
      <h1 className="font-display text-4xl text-white leading-none mb-8">
        <span className="italic text-gold">Commandes</span> entrantes
      </h1>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`text-xs uppercase tracking-wider px-3 py-2 border transition ${
            filter === "all" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-subtle text-gray-300"
          }`}
          data-testid="filter-all"
        >
          Toutes
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`text-xs uppercase tracking-wider px-3 py-2 border transition ${
              filter === s.key ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-subtle text-gray-300"
            }`}
            data-testid={`filter-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="bg-night-2 border border-subtle overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Chargement…</div>
        ) : orders.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Aucune commande.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-subtle">
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500">
                <th className="p-4">N°</th>
                <th className="p-4">Client</th>
                <th className="p-4 hidden sm:table-cell">Téléphone</th>
                <th className="p-4 hidden md:table-cell">Zone</th>
                <th className="p-4 hidden lg:table-cell">Créneau</th>
                <th className="p-4">Total</th>
                <th className="p-4">Statut</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => setSelected(o)}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  data-testid={`admin-order-row-${o.id}`}
                >
                  <td className="p-4 font-mono text-xs">{o.order_number}</td>
                  <td className="p-4">{o.customer_name}</td>
                  <td className="p-4 hidden sm:table-cell">{o.customer_phone}</td>
                  <td className="p-4 hidden md:table-cell">{o.delivery_zone}</td>
                  <td className="p-4 hidden lg:table-cell">{o.delivery_time}</td>
                  <td className="p-4 text-gold">{formatPrice(o.total)}</td>
                  <td className="p-4">
                    <select
                      value={o.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleStatusChange(o.id, e.target.value)}
                      className="bg-[#0a0f1a] border border-[#d4af37]/40 text-[#d4af37] text-[10px] uppercase tracking-wider px-2 py-1.5"
                      data-testid={`order-status-${o.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Drawer */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <aside
            className="fixed top-0 right-0 h-full w-full sm:w-[480px] bg-[#121824] border-l border-subtle z-50 overflow-y-auto"
            data-testid="order-detail-drawer"
          >
            <div className="flex items-center justify-between p-5 border-b border-subtle">
              <div>
                <p className="eyebrow">Commande</p>
                <h3 className="font-display text-2xl text-white">{selected.order_number}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="btn-ghost p-2" data-testid="close-detail-btn">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <p className="eyebrow mb-2">Client</p>
                <p className="text-lg text-white">{selected.customer_name}</p>
                <a href={`tel:${selected.customer_phone}`} className="flex items-center gap-2 mt-2 text-[#d4af37]">
                  <Phone size={14} /> {selected.customer_phone}
                </a>
                {selected.customer_email && (
                  <p className="text-sm text-gray-400 mt-1">{selected.customer_email}</p>
                )}
              </div>

              <div>
                <p className="eyebrow mb-2">Livraison</p>
                <p className="text-sm text-white flex items-start gap-2">
                  <MapPin size={14} className="mt-1 text-[#d4af37]" />
                  <span>{selected.delivery_address}<br /><span className="text-gray-400">{selected.delivery_zone}</span></span>
                </p>
                <p className="text-sm text-gray-300 flex items-center gap-2 mt-2">
                  <Clock size={14} className="text-[#d4af37]" />
                  {selected.delivery_time}
                </p>
              </div>

              {selected.notes && (
                <div>
                  <p className="eyebrow mb-2">Notes</p>
                  <p className="text-sm text-gray-300 italic">{selected.notes}</p>
                </div>
              )}

              <div>
                <p className="eyebrow mb-3">Articles</p>
                <ul className="space-y-2">
                  {selected.items.map((it, i) => (
                    <li key={i} className="flex justify-between text-sm border-b border-white/5 py-2">
                      <span className="text-gray-200">
                        <span className="text-[#d4af37] mr-2">{it.quantity}×</span>
                        {it.name}
                      </span>
                      <span className="text-white">{formatPrice(it.price * it.quantity)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-subtle pt-4 space-y-1 text-sm">
                <div className="flex justify-between text-gray-300">
                  <span>Sous-total</span>
                  <span>{formatPrice(selected.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Livraison</span>
                  <span>{formatPrice(selected.delivery_fee)}</span>
                </div>
                <div className="flex justify-between font-display text-2xl text-gold pt-2">
                  <span>Total</span>
                  <span>{formatPrice(selected.total)}</span>
                </div>
              </div>

              <div>
                <p className="eyebrow mb-2">Statut</p>
                <select
                  value={selected.status}
                  onChange={(e) => handleStatusChange(selected.id, e.target.value)}
                  className="input-line bg-[#0a0f1a] border border-[#d4af37]/40 px-3 py-2"
                  data-testid="detail-status-select"
                >
                  {STATUSES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default AdminOrders;
